import { useEffect, useContext, useMemo, useRef, useState } from "react";
import { MessageContext } from '../context/MessageContext';
import { AuthContext } from '../context/AuthContext';
import axios from "axios";
import io from 'socket.io-client';
/**
 * Chat.jsx
 * ---------------------------------------------
 * Компонент чата в стиле WeChat с Tailwind CSS.
 *
 * Особенности:
 * - Визуальный стиль, близкий к WeChat
 * - Сообщения Менеджера выравниваются справа, остальные — слева
 * - Адаптивное позиционирование и размеры:
 *   - Мобильные: фиксирован внизу, на всю ширину
 *   - Планшет/ПК: фиксирован справа внизу, компактная карточка
 * - Кнопка сворачивания/разворачивания с плавной анимацией
 * - Плавные переходы, hover/active состояния, корректный z-index
 * - Боковая панель для переключения чатов (только на десктопе/планшетах)
 * - На мобильных: список чатов в основной области, слайд-анимация при выборе чата
 *
 * Пропсы:
 * - offers: array — массив объектов предложений с offerId, reqId, modelName
 * */
let socketInstance = null; // 2. Переменная для хранения сокета

function Chat({ offers }) {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { setMess } = useContext(MessageContext);

  const chats = useMemo(() => offers
    .filter(offer => offer.published === 1)
    .map(offer => ({
      offerId: offer.id_car_offer,
      reqId: offer.id_car_request,
      modelName: offer.model_name_en
    })), [offers]);

  // Изменение: initial activeChatId = null для показа списка по умолчанию
  const [activeChatId, setActiveChatId] = useState(null);
  const [isOpen, setIsOpen] = useState(false); // состояние свёрнут/развёрнут
  const [isSending, setIsSending] = useState(false);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [socketError, setSocketError] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  // 5. Эффект для подключения к сокету и обработки сообщений
  useEffect(() => {
    if (!user?.id || !isAuthenticated) return; // Не подключаемся без пользователя и токена

    // 6. Создаем сокет-подключение
    if (process.env.REACT_APP_VPS_SERVER === '0')
      socketInstance = io('http://localhost:5001'); // URL вашего сервера
    else {
      socketInstance = io({
        path: "/socket.io",   // по умолчанию, но лучше явно указать
        transports: ["websocket", "polling"]
      });
    }

    // 7. Аутентификация сокета
    socketInstance.emit('authenticate', user.id);
    setSocketError(false);
    // 8. Слушатель получения нового сообщения
    socketInstance.on('receive_message', (message) => {
      console.log("Новое сообщение через сокет:", message);
      // 9. Обновляем состояние messages, добавляя новое сообщение
      setMessages(prevMessages => {
        // Проверка, чтобы не добавлять дубликаты (если сообщение уже есть)
        if (prevMessages.some(msg => msg.id_message === message.id_message)) {
          return prevMessages;
        }
        return [...prevMessages, message];
      });
    });

    socketInstance.emit('join_room_manager', { idManager: user.id });

    socketInstance.on('receive_message_manager', (message) => {
      console.log("Новое сообщение через сокет от сервера клиента:", message);
      setMess({ color: 'green', title: 'Уведомление', desc: message, duration: '-1' });
    });

    // 10. Обработка ошибок подключения
    socketInstance.on('connect_error', (err) => {
      console.error('Ошибка подключения к сокету:', err);
      setSocketError(true);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Сокет отключен:', reason);
      setSocketError(true);
    });

    // 11. Функция очистки при размонтировании компонента
    return () => {
      if (socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
        console.log("Сокет отключен при размонтировании компонента");
      }
    };
  }, [user?.id, isAuthenticated]); // 12. Зависимости: переподключаемся при смене пользователя/токена

  // 13. Эффект для присоединения/покидания комнаты при смене активного чата
  useEffect(() => {
    if (!socketInstance || !activeChatId) return;

    // 14. Покидаем предыдущую комнату (если была)
    const prevChatId = activeChatId; // Сохраняем для leave_room

    // 15. Присоединяемся к новой комнате
    socketInstance.emit('join_room', { offerId: activeChatId });
    console.log(`Присоединились к комнате offer_${activeChatId}`);
    setSocketError(false);

    // 16. Функция очистки для покидания комнаты
    return () => {
      if (socketInstance && prevChatId) {
        socketInstance.emit('leave_room', { offerId: prevChatId });
        console.log(`Покинули комнату offer_${prevChatId}`);
      }
    };
  }, [activeChatId]); // 17. Зависимость: эффект срабатывает при смене activeChatId

  // 18. Эффект для загрузки начальной истории сообщений при смене чата
  useEffect(() => {
    let ignore = false;
    async function fetchMessages() {
      if (!user?.id || !activeChatId) return;
      try {
        setLoading(true);
        const res = await axios.get(`/api/messages/${activeChatId}`);
        if (!ignore) setMessages(Array.isArray(res.data) && res.data.length > 0 ? res.data : []);
        setSocketError(false);
      } catch (e) {
        console.error("Ошибка получения сообщений:", e);
        if (!ignore) setMessages(""); // Очищаем в случае ошибки
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    fetchMessages();
    return () => {
      ignore = true;
    };
  }, [activeChatId, user?.id]);

  //20. Скроллимся к последнему сообщению
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isOpen, activeChatId]);

  // Отправка сообщения
  async function sendMessage() {
    const text = newMessage.trim();
    if (!text || !activeChatId) return;
    try {
      setIsSending(true);
      const activeChat = chats.find(c => c.offerId === activeChatId);
      await axios.post("/api/messages", {
        id_offer: activeChatId,
        id_request: activeChat.reqId,
        message: text,
      });
      setNewMessage("");
      // Обновим сообщения после отправки
      const res = await axios.get(`/api/messages/${activeChatId}`);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Ошибка отправки сообщения:", e);
    } finally {
      setIsSending(false);
    }
  }

  // keydown обработчик для textarea
  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  // Форматирование времени в стиле быстрых меток
  const formatTime = (d) => {
    try {
      const date = new Date(d);
      return date.toLocaleString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const activeChat = chats.find(c => c.offerId === activeChatId);

  // Визуальные хелперы для чата
  const headerTitle = useMemo(() => activeChat && isOpen ? `Чат #${String(activeChat.offerId).slice(-3)} • ${activeChat.modelName}` : "Чаты");

  const getShortId = (id) => String(id).slice(-3);

  if (!user)
    return (
      <section className="py-10">
        <div className="flex justify-center px-4">
          <div className="flex w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center w-12 bg-green-500">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="px-4 py-3">
              <div className="flex flex-col">
                <span className="text-green-500 dark:text-green-400">Требуется авторизация</span>
                <p className="text-sm text-gray-600 dark:text-gray-200">
                  Пожалуйста, войдите в систему, чтобы продолжить.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );

  if (chats.length === 0) return null;

  // Вынос списка чатов в отдельный компонент для переиспользования
  const ChatList = () => (
    <ul className="p-2 space-y-1">
      {chats.map((chat) => (
        <li key={chat.offerId}>
          <button
            onClick={() => setActiveChatId(chat.offerId)}
            className={[
              "w-full text-left px-2 py-1 rounded-md text-sm truncate cursor-pointer",
              activeChatId === chat.offerId
                ? "bg-green-500 hover:bg-green-600 dark:bg-green-700 dark:hover:bg-green-800 text-white"
                : "hover:bg-gray-200 dark:hover:bg-gray-700",
            ].join(" ")}
          >
            #{getShortId(chat.offerId)} • {chat.modelName}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={[
        // Позиционирование контейнера чата
        "fixed z-50",
        // Mobile: full-width bottom
        "inset-x-0 bottom-8 w-full px-3 pb-3",
        // Tablet/desktop: компактная карточка
        "md:inset-auto md:right-4 md:bottom-4 md:px-0 md:pb-0",
        isOpen ? "md:w-[48rem]" : "md:w-[12rem]",
        // Шрифты/anti-alias
        "font-sans antialiased transition-all ease-in-out duration-500"
      ].join(" ")}
      aria-live="polite"
    >
      <div
        className={[
          // Карточка
          "mx-auto md:mx-0 shadow-2xl rounded-md overflow-hidden",
          // Mobile на всю ширину
          "w-full flex",
          // Цвет подложки в развернутом виде (будет виден в зонах без сообщений)
          "bg-white/95 backdrop-bldur-sm dark:bg-gray-800",
          // Граница лёгкая, как у модальных
          "ring-1 ring-black/5 dark:ring-gray-700",
          // Анимация появления карточки
          "transition-transform duration-300 ease-out",
        ].join(" ")}
        style={{ transformOrigin: "bottom right" }}
      >
        {socketError ? (
          <div className="flex-1">
            {/* Шапка */}
            <div
              className={[
                "flex items-center gap-3 px-3 py-2",
                "bg-[#07C160] text-white dark:bg-gray-700",
              ].join(" ")}
            >
              <p>Ошибка сокета.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Sidebar (только на md+, без toggle на mobile) */}
            <div
              className={[
                isOpen ? "hidden md:block" : "hidden", //скрыта на mobile, видна/скрыта на md+
                "w-32 md:w-48 bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto",
              ].join(" ")}
            >
              <ChatList />
            </div>

            {/* Основная область чата */}
            <div className="flex-1">
              {/* Шапка */}
              <div
                className={[
                  "flex items-center gap-3 px-3 py-2",
                  "bg-[#07C160] text-white dark:bg-gray-700",
                ].join(" ")}
              >
                {/* Кнопка "Назад" на mobile при активном чате */}
                {activeChatId && (
                  <button
                    type="button"
                    onClick={() => { setMessages(""); setActiveChatId(null); }}
                    className="md:hidden inline-flex h-8 w-8 items-center justify-center rounded-xl hover:bg-white/10 active:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 dark:focus:ring-gray-600 cursor-pointer transition"
                    aria-label="Назад к списку чатов"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
                      <path d="M15.41 16.59 10.83 12l4.58-4.59a1 1 0 1 0-1.42-1.42l-5.29 5.3a1 1 0 0 0 0 1.42l5.29 5.3a1 1 0 0 0 1.42-1.42Z" />
                    </svg>
                  </button>
                )}

                {/* Аватар комнаты/менеджера */}
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 dark:bg-gray-600">
                  {/* Иконка WeChat-подобная (двойной чат) */}
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
                    <path d="M4 10a6 6 0 1 1 12 0 6 6 0 0 1-12 0Zm4.5 1.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm3 0a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" />
                    <path d="M14.5 13.5a5.5 5.5 0 1 1 9.5 3.915l.5 2.835-2.835-.5A5.5 5.5 0 0 1 14.5 13.5Zm2.75 1a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm3 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z" />
                  </svg>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-medium leading-tight dark:text-gray-200">{headerTitle}</div>
                  {/* <div className="truncate text-[11px] opacity-90 dark:text-gray-400">Онлайн • поддержка</div> */}
                </div>

                <div className="flex items-center gap-1">
                  {/* Скрыть/Показать чат */}
                  <button
                    type="button"
                    onClick={() => setIsOpen((v) => !v)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-xl hover:bg-white/10 active:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 dark:focus:ring-gray-600 cursor-pointer transition"
                    aria-label={isOpen ? "Свернуть чат" : "Развернуть чат"}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className={["h-5 w-5 transition-transform duration-300", isOpen ? "rotate-0" : "-rotate-180"].join(" ")}
                      fill="currentColor"
                      aria-hidden
                    >
                      {/* chevron-down */}
                      <path d="M6.7 9.3a1 1 0 0 1 1.4 0L12 13.17l3.9-3.87a1 1 0 1 1 1.4 1.43l-4.6 4.55a1.5 1.5 0 0 1-2.1 0L6.7 10.7a1 1 0 0 1 0-1.4Z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Тело чата: сворачивается по высоте */}
              <div>
                {/* Контейнер сообщений */}
                <div
                  className={[
                    "overflow-hidden transition-all duration-500 ease-in-out", // для анимации высоты
                    isOpen ? 'max-h-[500px]' : 'max-h-0'
                  ].join(" ")}
                >
                  <div
                    className={[
                      // Область сообщений
                      "relative h-[30vh] md:h-[22rem] lg:h-[26rem] overflow-y-auto overflow-x-hidden", // Добавлен overflow-x-hidden для анимации
                      "px-3 py-3",
                      // Фон в духе WeChat — очень светло-серый
                      "bg-[#ededed] dark:bg-gray-900",
                    ].join(" ")}
                  >
                    {/* Панель списка чатов (только на mobile, когда activeChatId === null) */}
                    <div
                      className={[
                        "absolute top-0 left-0 w-1/2 h-[30vh] transition-transform duration-300 ease-in-out",
                        activeChatId ? "-translate-x-full" : "translate-x-0",
                        "md:hidden", // Скрыто на десктопе
                      ].join(" ")}
                    >
                      <ChatList />
                    </div>
                    {!activeChatId && (
                      <div className="hidden md:block text-center text-gray-500 dark:text-gray-400 ">
                        Выберите чат из списка слева
                      </div>
                    )}
                    {/* Панель сообщений чата */}
                    {activeChatId && (
                      <div
                        className={[
                          "absolute top-0 left-0 w-full h-full p-3 transition-transform duration-300 ease-in-out",
                          activeChatId ? "translate-x-0" : "translate-x-full",
                        ].join(" ")}
                      >
                        {loading ? (
                          // Скелетон загрузки
                          <div className="space-y-3 animate-pulse">
                            <div className="h-4 w-1/3 rounded bg-black/10 dark:bg-gray-700" />
                            <div className="h-16 w-2/3 rounded-xl bg-black/10 dark:bg-gray-700" />
                            <div className="h-16 w-1/2 rounded-xl bg-black/10 dark:bg-gray-700 ml-auto" />
                          </div>
                        ) : Array.isArray(messages) && messages.length > 0 ? (
                          <ul className="space-y-2">
                            {messages.map((msg) => {
                              const isManager = msg?.sender_user_id === null || msg?.sender_user_id === undefined;
                              // Выравнивание и цвета в зависимости от отправителя
                              const side = isManager ? "right" : "left";
                              return (
                                <li key={msg.id_message} className={["flex", side === "right" ? "justify-end" : "justify-start"].join(" ")}>
                                  <div className={["flex max-w-[85%] items-end gap-2 md:max-w-[80%]", side === "right" ? "flex-row-reverse" : "flex-row"].join(" ")}>
                                    {/* Аватар */}
                                    <div className="h-7 w-7 shrink-0 rounded-full bg-white ring-1 ring-black/5 dark:ring-gray-700 grid place-items-center text-[10px] text-black/60 dark:text-gray-600">
                                      {msg?.sender_name?.[0]?.toUpperCase() ?? "?"}
                                    </div>

                                    {/* Пузырь сообщения */}
                                    <div
                                      className={[
                                        "rounded-2xl px-3 py-2 text-[15px] leading-snug shadow",
                                        side === "right" ? "bg-[#95ec69] text-[#111] dark:bg-green-800" : "bg-[#f5f5f5] text-[#111] dark:bg-gray-800",
                                        // Хвост пузыря (скошенный уголок)
                                        side === "right" ? "rounded-br-sm" : "rounded-bl-sm",
                                      ].join(" ")}
                                    >
                                      {/* Текст сообщения */}
                                      <div className="whitespace-pre-wrap break-words dark:text-gray-200">{msg.message}</div>

                                      {/* Время + статус прочтения (для исходящих) */}
                                      <div
                                        className={[
                                          "mt-1 text-[11px] opacity-60 flex items-center gap-1",
                                          side === "right" ? "justify-end" : "justify-start",
                                        ].join(" ")}
                                      >
                                        <span className="dark:text-gray-200">{formatTime(msg.created_at)}</span>

                                        {side === "right" && (
                                          msg.is_read ? (
                                            // Две зелёные галочки
                                            <svg
                                              viewBox="0 0 24 24"
                                              className="h-4 w-4 text-[#07C160] dark:text-green-400"
                                              fill="currentColor"
                                            >
                                              <path d="M9.29 16.29 4.7 11.7a1 1 0 0 1 1.42-1.42L10 14.17l8.88-8.88a1 1 0 0 1 1.41 1.42l-9.59 9.58a1 1 0 0 1-1.41 0Z" />
                                              <path d="M14.29 16.29 9.7 11.7a1 1 0 0 1 1.42-1.42L15 14.17l6.88-6.88a1 1 0 0 1 1.41 1.42l-7.59 7.58a1 1 0 0 1-1.41 0Z" />
                                            </svg>
                                          ) : (
                                            // Одна серая галочка
                                            <svg
                                              viewBox="0 0 24 24"
                                              className="h-4 w-4 text-black/40 dark:text-gray-200"
                                              fill="currentColor"
                                            >
                                              <path d="M9.29 16.29 4.7 11.7a1 1 0 0 1 1.42-1.42L10 14.17l8.88-8.88a1 1 0 0 1 1.41 1.42l-9.59 9.58a1 1 0 0 1-1.41 0Z" />
                                            </svg>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}

                            {/* Якорь для авто-скролла */}
                            <div ref={endRef} />
                          </ul>
                        ) : (
                          <div className="text-center text-red-500">
                            В чате ещё нет сообщений.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                {/* Поле ввода + кнопка отправки (показывается только когда открыт и есть активный чат) */}
                {/* {isOpen && ( */}
                  <div className="border-t border-black/5 bg-white px-3 py-2 dark:bg-gray-800">
                    <div className="flex items-end gap-2">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Введите сообщение…"
                        rows={1}
                        className="min-h-[2.5rem] max-h-28 w-full resize-none rounded-md border border-black/10 bg-white px-3 py-2 text-[15px] leading-snug shadow-sm outline-none placeholder:text-black/40 focus:border-[#07C160]/50 focus:ring-2 focus:ring-[#07C160]/30 transition dark:bg-gray-700 dark:text-gray-200 dark:placeholder:text-gray-400 dark:focus:ring-green-500"
                      />
                      <button
                        type="button"
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || isSending || !activeChatId}
                        className="inline-flex h-10 shrink-0 items-center justify-center rounded-md px-4 text-sm text-white buttonGreen active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow focus:outline-none focus:ring-2 focus:ring-[#07C160]/40 dark:focus:ring-green-400 transition-colors duration-200"
                      >
                        {/* Иконка paper-plane */}
                        <svg viewBox="0 0 24 24" className="mr-1 h-4 w-4" fill="currentColor" aria-hidden>
                          <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2 .01 7Z" />
                        </svg>
                        Отправить
                      </button>
                    </div>
                  </div>
                {/* )} */}
                </div>

              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Chat;